import { useEffect, useState } from 'react'
import { api, type JobStatus } from '../lib/api.ts'
import { Button } from '../components/Button.tsx'
import { icons } from '../components/icons.ts'
import { useToast } from '../components/Toast.tsx'

/**
 * The scheduled work, and a way to start it.
 *
 * Registering a schedule was always meant to be a deliberate act rather than
 * something a deploy does on its own -- a pod restart must not resurrect a
 * schedule somebody paused during an incident. But it had been implemented as
 * *impossible* rather than deliberate: nothing called the registration
 * function, so starting a sync needed a machine with Temporal credentials and
 * somebody who knew the function existed.
 *
 * ## The two dates, side by side
 *
 * `Última execução` comes from Temporal and `Catálogo atualizado` from our own
 * database, and showing both is the point. A schedule that fires and a
 * catalogue that is current are different facts, and the gap between them is
 * exactly what a failing sync looks like from outside: it ran, and nothing
 * changed.
 */
export function Jobs({ onBack }: { onBack: () => void }) {
  const [jobs, setJobs] = useState<JobStatus[] | undefined>()
  const [available, setAvailable] = useState(true)
  const [busy, setBusy] = useState<string | undefined>()
  const [error, setError] = useState<string | undefined>()
  const toast = useToast()

  function load() {
    void api
      .jobs()
      .then((r) => {
        setJobs(r.jobs)
        setAvailable(r.available)
      })
      .catch((cause: unknown) =>
        setError(cause instanceof Error ? cause.message : 'falha ao carregar'),
      )
  }

  useEffect(load, [])

  async function act(
    accountId: string,
    what: string,
    run: () => Promise<unknown>,
    done: string,
  ): Promise<void> {
    setBusy(`${accountId}:${what}`)
    try {
      await run()
      toast.show(done, { tone: 'success' })
      load()
    } catch (cause) {
      toast.show('Não foi possível concluir', {
        tone: 'error',
        detail: cause instanceof Error ? cause.message : undefined,
      })
    } finally {
      setBusy(undefined)
    }
  }

  return (
    <div className="flex flex-col gap-5 p-5 pt-0">
      <div className="flex flex-wrap items-center gap-3">
        <Button
          variant="tertiary"
          tone="neutral"
          size="small"
          leadingIcon={icons.back}
          onClick={onBack}
        >
          Fila
        </Button>
        <h1 className="text-tH4 text-text-strong">Rotinas</h1>
        <span className="bg-fill-brand-weak text-text-brand rounded-base text-tTiny px-2 py-0.5 font-semibold">
          interno
        </span>
      </div>

      <p className="text-tP text-text-weak max-w-prose">
        Estas rotinas leem a base da farmácia — só leem, nunca escrevem. <strong>Registrar</strong>{' '}
        cria o agendamento; <strong>Rodar agora</strong> executa uma vez, que é como a primeira
        conversa com a base de um cliente deveria acontecer: com alguém olhando, e não de madrugada.
      </p>
      <p className="text-tSmall text-text-weak max-w-prose">
        Descobrir um orçamento não custa nada: a conferência fica esperando até o governador
        liberar, e ele mantém um número fixo de conferências prontas — quantas é uma configuração do
        ambiente, e dizer um número aqui só envelhece mal. Um lugar vaga quando alguém confere, não
        quando a análise termina — então nada é analisado se ninguém está conferindo, e a fila parar
        de noite é o comportamento correto.
      </p>

      {!available && (
        <p className="text-tP text-text-warning">
          Sem conexão com o Temporal. Os catálogos abaixo ainda mostram quando foram atualizados,
          mas nada pode ser iniciado daqui.
        </p>
      )}

      {error !== undefined && <p className="text-tP text-text-error">{error}</p>}
      {jobs === undefined && error === undefined && (
        <p className="text-tP text-text-weak" role="status" aria-live="polite">
          Carregando…
        </p>
      )}

      {jobs !== undefined && (
        <div className="rounded-base border-stroke-weak bg-background-raised w-full overflow-x-auto border">
          <table className="w-full border-collapse">
            <thead>
              <tr>
                {[
                  'Rotina',
                  'Farmácia',
                  'Agendamento',
                  'Última execução',
                  'Catálogo atualizado',
                  '',
                ].map((h) => (
                  <th
                    key={h}
                    scope="col"
                    className="border-stroke-weak bg-background-sunken text-tPSemi text-text-strong border-b px-3 py-3 text-left"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {jobs.map((job) => (
                <tr key={`${job.kind}-${job.accountId}`}>
                  <td className="border-stroke-weak border-b px-3 py-3">
                    <span className="text-tP text-text-strong">{job.title}</span>
                    <span className="text-tTiny text-text-weak mt-0.5 block max-w-prose">
                      {job.description}
                    </span>
                  </td>
                  <td className="border-stroke-weak text-tP text-text-strong border-b px-3 py-3">
                    {job.accountName}
                  </td>
                  <td className="border-stroke-weak text-tP border-b px-3 py-3">
                    {!job.registered ? (
                      <span className="text-text-weak">não registrado</span>
                    ) : job.paused ? (
                      <span className="text-text-warning">pausado</span>
                    ) : (
                      <span className="text-text-weak">{job.cron ?? 'agendado'}</span>
                    )}
                  </td>
                  <td className="border-stroke-weak text-tP text-text-weak border-b px-3 py-3">
                    {when(job.lastRunAt)}
                  </td>
                  {/*
                    Amber when the catalogue has never been refreshed. An verification
                    started against an empty replica parks on
                    `awaiting_catalog` rather than passing, so this is the
                    column that explains why nothing is moving -- including for
                    quotations the poll has just discovered.
                  */}
                  <td
                    className={`border-stroke-weak text-tP border-b px-3 py-3 ${
                      job.catalogSyncedAt === undefined ? 'text-text-warning' : 'text-text-weak'
                    }`}
                  >
                    {job.catalogSyncedAt === undefined ? 'nunca' : when(job.catalogSyncedAt)}
                  </td>
                  <td className="border-stroke-weak border-b px-3 py-3">
                    <div className="flex flex-wrap gap-2">
                      <Button
                        variant="secondary"
                        tone="neutral"
                        size="small"
                        disabled={!available || busy !== undefined}
                        title="Cria ou atualiza o agendamento diário. Nunca despausa um agendamento que alguém pausou."
                        onClick={() =>
                          void act(
                            job.accountId,
                            'register',
                            () => api.registerJob(job.kind, job.accountId),
                            `${job.title} · ${job.accountName}: agendamento registrado`,
                          )
                        }
                      >
                        {job.registered ? 'Atualizar' : 'Registrar'}
                      </Button>

                      {job.running ? (
                        /*
                          A run in flight, in place of the button that would
                          start one.

                          Offering "Rodar agora" here offers something that
                          cannot happen: the run holds the job's id, so a press
                          is refused rather than queued. Showing its status
                          instead answers the question somebody actually has --
                          is it still going -- and hides the way to stop it
                          behind a deliberate hover, because abandoning a run
                          that is merely slow is how a sync gets killed halfway
                          for no reason.
                        */
                        <span className="group relative inline-flex">
                          <Button
                            variant="secondary"
                            tone="neutral"
                            size="small"
                            disabled={!available || busy !== undefined}
                            title={
                              job.runningSince === undefined
                                ? 'Uma execução está em andamento'
                                : `Em execução desde ${new Date(job.runningSince).toLocaleString('pt-BR')} — clique para abandonar`
                            }
                            onClick={() =>
                              void act(
                                job.accountId,
                                'cancel',
                                () => api.cancelJob(job.kind, job.accountId),
                                `${job.title} · ${job.accountName}: execução liberada`,
                              )
                            }
                          >
                            {/* Two labels in one control: what it is, and what
                                clicking does. The second only on hover or
                                keyboard focus, so it cannot be hit blind. */}
                            <span className="group-hover:hidden group-focus-within:hidden">
                              ● Em execução
                            </span>
                            <span className="hidden group-hover:inline group-focus-within:inline">
                              Abandonar
                            </span>
                          </Button>
                        </span>
                      ) : (
                        <Button
                          variant="primary"
                          tone="brand"
                          size="small"
                          disabled={!available || busy !== undefined}
                          title="Executa uma sincronização agora, contra a base da farmácia"
                          onClick={() =>
                            void act(
                              job.accountId,
                              'run',
                              () => api.runJob(job.kind, job.accountId),
                              `${job.title} · ${job.accountName}: iniciada — acompanhe os logs e as colunas abaixo`,
                            )
                          }
                        >
                          Rodar agora
                        </Button>
                      )}

                      {/*
                        The schedule's own pass, which is not a reason to
                        withhold anything.

                        Discovery spends four and a half minutes of every five
                        inside a pass, so this is the ordinary state and reads
                        as one: a quiet dot beside the buttons rather than in
                        place of them. It becomes the way to abandon a pass
                        only on hover, because a pass that is merely working
                        is not one anybody should stop by accident -- and a
                        pass that cannot finish holds the schedule behind it,
                        which is the case this exists for.
                      */}
                      {!job.running && job.scheduledSince !== undefined && (
                        <span className="group relative inline-flex">
                          <Button
                            variant="tertiary"
                            tone="neutral"
                            size="small"
                            disabled={!available || busy !== undefined}
                            title={`Passagem agendada em andamento desde ${new Date(job.scheduledSince).toLocaleString('pt-BR')} — clique para abandonar`}
                            onClick={() =>
                              void act(
                                job.accountId,
                                'cancel',
                                () => api.cancelJob(job.kind, job.accountId),
                                `${job.title} · ${job.accountName}: execução liberada`,
                              )
                            }
                          >
                            <span className="group-hover:hidden group-focus-within:hidden">
                              ● agendada em execução
                            </span>
                            <span className="hidden group-hover:inline group-focus-within:inline">
                              Abandonar
                            </span>
                          </Button>
                        </span>
                      )}

                      {job.registered && (
                        <Button
                          variant="tertiary"
                          tone={job.paused ? 'brand' : 'danger'}
                          size="small"
                          disabled={!available || busy !== undefined}
                          title={
                            job.paused
                              ? 'Volta a rodar no horário agendado'
                              : 'Para o agendamento — o jeito de não martelar a base da farmácia durante um incidente'
                          }
                          onClick={() =>
                            void act(
                              job.accountId,
                              'pause',
                              () => api.pauseJob(job.kind, job.accountId, !job.paused),
                              job.paused
                                ? `${job.title} · ${job.accountName}: retomado`
                                : `${job.title} · ${job.accountName}: pausado`,
                            )
                          }
                        >
                          {job.paused ? 'Retomar' : 'Pausar'}
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {jobs !== undefined && <FetchQuotation accounts={accountsOf(jobs)} onDone={load} />}
      <PurgeOneVerification onDone={load} />
      {jobs !== undefined && <PurgeVerifications accounts={accountsOf(jobs)} onDone={load} />}
    </div>
  )
}

/**
 * Emptying an account's verifications, so the same quotations can be re-verified.
 *
 * Here rather than on its own screen because this is the operator's page and
 * the thing it destroys is what the rest of this page produces. Temporary: the
 * day a pharmacist's conferência is real work, this is not a maintenance tool
 * but a way to destroy it, and it should go with the pre-launch period it
 * belongs to.
 */
/**
 * Verifications one quotation by number, now.
 *
 * Discovery finds quotations by date and admits them as the buffer allows;
 * this is the same enrolment for one the operator names, pre-admitted --
 * to look at a case, or to re-run one after a fix without waiting for the
 * next pass.
 */
function FetchQuotation({
  accounts,
  onDone,
}: {
  accounts: ReadonlyArray<{ slug: string; name: string }>
  onDone: () => void
}) {
  const [slug, setSlug] = useState(accounts[0]?.slug ?? '')
  const [branch, setBranch] = useState('')
  const [number, setNumber] = useState('')
  const [busy, setBusy] = useState(false)
  const toast = useToast()

  const branchCode = Number(branch)
  const quotationNumber = Number(number)
  const ready =
    slug !== '' &&
    Number.isInteger(branchCode) &&
    Number.isInteger(quotationNumber) &&
    number !== ''

  async function fetch() {
    if (!ready) return
    setBusy(true)
    try {
      const result = await api.fetchQuotation(slug, branchCode, quotationNumber)
      toast.show(
        `Orçamento ${quotationNumber} entrou na fila — conferência ${result.verificationId.slice(0, 8)}`,
      )
      setNumber('')
      onDone()
    } catch (cause) {
      toast.show(cause instanceof Error ? cause.message : 'falha ao buscar')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="rounded-base border-stroke-weak bg-background-raised flex flex-col gap-3 border p-4">
      <div className="flex flex-col">
        <span className="text-tPSemi text-text-strong">Buscar orçamento</span>
        <span className="text-tSmall text-text-weak max-w-prose">
          Confere um orçamento pelo número, agora, sem esperar a descoberta. Se ele já tem
          conferência, nada é iniciado — use "tentar de novo" na conferência ou limpe-a antes.
        </span>
      </div>
      <div className="flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1">
          <span className="text-tSmall text-text-weak">Conta</span>
          <select
            value={slug}
            onChange={(event) => setSlug(event.target.value)}
            className="rounded-base border-stroke-weak text-tP border px-2 py-1"
          >
            {accounts.map((one) => (
              <option key={one.slug} value={one.slug}>
                {one.name}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-tSmall text-text-weak">Filial</span>
          <input
            value={branch}
            onChange={(event) => setBranch(event.target.value)}
            placeholder="6"
            inputMode="numeric"
            className="rounded-base border-stroke-weak text-tP w-20 border px-2 py-1"
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-tSmall text-text-weak">Nº do orçamento</span>
          <input
            value={number}
            onChange={(event) => setNumber(event.target.value)}
            placeholder="832336"
            inputMode="numeric"
            className="rounded-base border-stroke-weak text-tP w-32 border px-2 py-1"
          />
        </label>
        <Button
          variant="primary"
          size="small"
          disabled={busy || !ready}
          title={ready ? `Confere ${branch}/${number} agora` : 'Informe filial e número'}
          onClick={() => void fetch()}
        >
          {busy ? 'Buscando…' : 'Buscar'}
        </Button>
      </div>
    </div>
  )
}

/** One verification, gone: its analysis stopped and its row deleted. */
function PurgeOneVerification({ onDone }: { onDone: () => void }) {
  const [id, setId] = useState('')
  const [busy, setBusy] = useState(false)
  const toast = useToast()
  const ready = /^[0-9a-f-]{36}$/i.test(id.trim())

  async function purge() {
    if (!ready) return
    setBusy(true)
    try {
      const result = await api.purgeVerification(id.trim())
      toast.show(
        `Conferência ${result.deleted.slice(0, 8)} apagada${result.terminated ? ', análise parada' : ''}`,
      )
      setId('')
      onDone()
    } catch (cause) {
      toast.show(cause instanceof Error ? cause.message : 'falha ao limpar')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="rounded-base border-stroke-weak bg-background-raised flex flex-col gap-3 border p-4">
      <div className="flex flex-col">
        <span className="text-tPSemi text-text-strong">Limpar uma conferência</span>
        <span className="text-tSmall text-text-weak max-w-prose">
          Para a análise, se estiver rodando, e apaga a conferência. A descoberta não a reoferece —
          use "Buscar orçamento" para conferi-la de novo.
        </span>
      </div>
      <div className="flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1">
          <span className="text-tSmall text-text-weak">Identificador da conferência</span>
          <input
            value={id}
            onChange={(event) => setId(event.target.value)}
            placeholder="492b2c56-216f-4b3a-bd09-4a7132095919"
            className="rounded-base border-stroke-weak text-tP w-80 border px-2 py-1 font-mono"
          />
        </label>
        <Button
          variant="primary"
          tone="danger"
          size="small"
          disabled={busy || !ready}
          title={ready ? 'Apaga esta conferência' : 'Cole o identificador completo'}
          onClick={() => void purge()}
        >
          {busy ? 'Limpando…' : 'Limpar'}
        </Button>
      </div>
    </div>
  )
}

function PurgeVerifications({
  accounts,
  onDone,
}: {
  accounts: ReadonlyArray<{ slug: string; name: string }>
  onDone: () => void
}) {
  const [slug, setSlug] = useState('')
  const [since, setSince] = useState(new Date().toISOString().slice(0, 10))
  const [keep, setKeep] = useState('')
  const [busy, setBusy] = useState(false)
  const toast = useToast()

  const target = accounts.find((one) => one.slug === slug.trim())

  async function purge() {
    if (target === undefined) return
    setBusy(true)
    try {
      const numbers = keep
        .split(',')
        .map((one) => Number(one.trim()))
        .filter((one) => Number.isFinite(one))
      const result = await api.purgeVerifications(target.slug, `${since}T00:00:00Z`, numbers)
      toast.show(
        `${target.name}: ${result.deleted} conferências apagadas, ${result.terminated} análises paradas — a descoberta recomeça de ${since}`,
      )
      setSlug('')
      onDone()
    } catch (cause) {
      toast.show(cause instanceof Error ? cause.message : 'falha ao limpar')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="rounded-base border-stroke-weak bg-background-raised flex flex-col gap-3 border p-4">
      <div className="flex flex-col">
        <span className="text-tPSemi text-text-strong">Limpar conferências</span>
        <span className="text-tSmall text-text-weak max-w-prose">
          Apaga todas as conferências da conta e devolve a descoberta para a data escolhida, para
          que os mesmos orçamentos sejam conferidos de novo. Sem a data, a fila ficaria vazia para
          sempre. Provisório, enquanto estamos subindo o ambiente.
        </span>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1">
          <span className="text-tSmall text-text-weak">
            Conta — digite o identificador para confirmar
          </span>
          <input
            value={slug}
            onChange={(event) => setSlug(event.target.value)}
            placeholder={accounts[0]?.slug ?? 'identificador'}
            className="rounded-base border-stroke-weak text-tP w-56 border px-2 py-1"
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-tSmall text-text-weak">Redescobrir a partir de</span>
          <input
            type="date"
            value={since}
            onChange={(event) => setSince(event.target.value)}
            className="rounded-base border-stroke-weak text-tP border px-2 py-1"
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-tSmall text-text-weak">Manter (nº do orçamento)</span>
          <input
            value={keep}
            onChange={(event) => setKeep(event.target.value)}
            placeholder="-3, -4"
            className="rounded-base border-stroke-weak text-tP w-40 border px-2 py-1"
          />
        </label>
        <Button
          variant="primary"
          tone="danger"
          size="small"
          disabled={busy || target === undefined}
          title={
            target === undefined
              ? 'Digite o identificador exato da conta para confirmar'
              : `Apaga as conferências de ${target.name}`
          }
          onClick={() => void purge()}
        >
          {busy ? 'Limpando…' : 'Limpar'}
        </Button>
      </div>
    </div>
  )
}

/** The accounts this page already knows about, one row each. */
function accountsOf(jobs: readonly JobStatus[]): ReadonlyArray<{ slug: string; name: string }> {
  const seen = new Map<string, string>()
  for (const job of jobs) {
    seen.set(job.accountSlug, job.accountName)
  }
  return [...seen].map(([slug, name]) => ({ slug, name }))
}

/** Local time: the reader is asking "was that before or after my change?" */
function when(iso: string | undefined): string {
  if (iso === undefined) return '—'
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString('pt-BR')
}
